import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { NotificationView } from '../../api/generated/models/NotificationView';
import { assigneeAvatarColor } from '../../utils/assigneeHelpers';
import {
  extractTaskId,
  formatNotificationTime,
  notificationRowBody,
  notificationRowTitle,
} from '../../utils/notificationFeedHelpers';
import type { AppDispatch, RootState } from '../../store/store';
import {
  fetchNotificationFeed,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../store/slices/notificationFeedSlice';
import bellIcon from '../../assets/icons/Icon-Header-bell.svg';
import { ReactComponent as ChevronLeftIcon } from '../../assets/icons/Chevron-Left.svg';

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  onOpenTask: (taskId: string) => void;
  isMobile: boolean;
  /** Clicks here should not count as "outside" (bell toggle lives in this subtree). */
  outsideIgnoreRef?: React.RefObject<HTMLElement | null>;
}

const BellGlyph: React.FC = () => (
  <img src={bellIcon} alt="" className="notif-panel__bell-icon" aria-hidden="true" />
);

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  open,
  onClose,
  onOpenTask,
  isMobile,
  outsideIgnoreRef,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const panelRef = useRef<HTMLDivElement>(null);
  const unreadBaselineRef = useRef(0);
  const { items, loading, unreadCount } = useSelector(
    (state: RootState) => state.notificationFeed,
  );

  useEffect(() => {
    if (!open) {
      unreadBaselineRef.current = unreadCount;
      return;
    }
    unreadBaselineRef.current = unreadCount;
    void dispatch(fetchNotificationFeed({ includeItems: true }));
  }, [dispatch, open]);

  // Badge poll can bump unreadCount while the panel stays open — refresh the list too.
  useEffect(() => {
    if (!open || unreadCount === unreadBaselineRef.current) {
      return;
    }
    unreadBaselineRef.current = unreadCount;
    void dispatch(fetchNotificationFeed({ includeItems: true }));
  }, [dispatch, open, unreadCount]);

  useEffect(() => {
    if (!open || isMobile) {
      return undefined;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) {
        return;
      }
      if (outsideIgnoreRef?.current?.contains(target)) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, isMobile, onClose, outsideIgnoreRef]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleMarkAll = () => {
    if (unreadCount <= 0) {
      return;
    }
    void dispatch(markAllNotificationsRead());
  };

  const handleRowClick = (item: NotificationView) => {
    const taskId = extractTaskId(item);
    if (item.id && !item.isRead) {
      void dispatch(markNotificationRead(item.id));
    } else if (item.id && item.isRead) {
      // Already read — still navigate.
    }
    onClose();
    if (taskId) {
      onOpenTask(taskId);
    }
  };

  const panelInner = (
    <div
      ref={panelRef}
      className={`notif-panel${isMobile ? ' notif-panel--mobile' : ''}`}
      role="dialog"
      aria-label={t('notifications.bell.title')}
    >
      <header className="notif-panel__header">
        {isMobile ? (
          <div className="notif-panel__header-start">
            <button
              type="button"
              className="notif-panel__back"
              onClick={onClose}
              aria-label={t('back')}
            >
              <ChevronLeftIcon className="notif-panel__back-icon" aria-hidden="true" />
            </button>
            <button type="button" className="notif-panel__title-btn" onClick={onClose}>
              {t('notifications.bell.title')}
            </button>
          </div>
        ) : (
          <h2 className="notif-panel__title">{t('notifications.bell.title')}</h2>
        )}
        <button
          type="button"
          className="notif-panel__mark-all"
          onClick={handleMarkAll}
          disabled={unreadCount <= 0}
        >
          {t('notifications.bell.mark_all_read')}
        </button>
      </header>

      <div className="notif-panel__body">
        {loading && items.length === 0 ? (
          <div className="notif-panel__loading">
            <div className="loading-spinner" aria-label={t('loading_text')} />
          </div>
        ) : items.length === 0 ? (
          <div className="notif-panel__empty">
            <span className="notif-panel__empty-icon" aria-hidden="true">
              <BellGlyph />
            </span>
            <p className="notif-panel__empty-title">{t('notifications.bell.empty_title')}</p>
            <p className="notif-panel__empty-body">{t('notifications.bell.empty_body')}</p>
          </div>
        ) : (
          <ul className="notif-panel__list">
            {items.map((item, index) => {
              const initials = item.actorInitials?.trim();
              const showAvatar = Boolean(initials);
              const unread = !item.isRead;
              return (
                <li key={item.id ?? index}>
                  {index > 0 && <hr className="notif-panel__divider" />}
                  <button
                    type="button"
                    className={`notif-panel__row${unread ? ' notif-panel__row--unread' : ''}`}
                    onClick={() => handleRowClick(item)}
                  >
                    {showAvatar ? (
                      <span
                        className="notif-panel__avatar"
                        style={{
                          backgroundColor: assigneeAvatarColor(item.actorName ?? item.id ?? ''),
                        }}
                        aria-hidden="true"
                      >
                        {initials}
                      </span>
                    ) : (
                      <span className="notif-panel__avatar notif-panel__avatar--system" aria-hidden="true">
                        <BellGlyph />
                      </span>
                    )}
                    <span className="notif-panel__row-text">
                      <span className="notif-panel__row-title">{notificationRowTitle(item, t)}</span>
                      <span className="notif-panel__row-body">{notificationRowBody(item, t)}</span>
                      <span className="notif-panel__row-time">
                        {formatNotificationTime(item.createdAt, t)}
                      </span>
                    </span>
                    {unread && <span className="notif-panel__unread-dot" aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer className="notif-panel__footer">
        <Link to="/profile?tab=notifications" className="notif-panel__settings-link" onClick={onClose}>
          {t('notifications.bell.settings_link')}
        </Link>
      </footer>
    </div>
  );

  if (isMobile) {
    return (
      <div className="notif-panel-mobile-overlay" role="presentation">
        {panelInner}
      </div>
    );
  }

  return panelInner;
};

export default NotificationPanel;
